/**
 * FileGrid.tsx — Responsive tile grid for patient files/folders.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Folder, File, FileText, FileSpreadsheet, Film, Music, Archive } from 'lucide-react';
import type { PatientFile } from '@/services/patient-files.service';
import { fetchThumbnailBlobUrl, MAX_NAME_BYTES } from '@/services/patient-files.service';

const ICON_MAP: Record<string, typeof File> = {
  folder: Folder, pdf: FileText, doc: FileText, xls: FileSpreadsheet,
  ppt: FileText, csv: FileSpreadsheet, txt: FileText, video: Film,
  audio: Music, archive: Archive, file: File,
};

function formatSize(bytes: number | null): string {
  if (bytes == null || bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function ThumbnailImage({ patientId, file, folder }: { patientId: number; file: PatientFile; folder: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchThumbnailBlobUrl(patientId, file.name, folder || undefined, file.source).then((url) => {
      if (!cancelled) setBlobUrl(url);
    });
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [patientId, file.name, file.source, folder]);

  if (failed || !blobUrl) {
    const Icon = ICON_MAP[file.icon] ?? File;
    return <Icon className="h-12 w-12 text-slate-400" />;
  }

  return (
    <img
      src={blobUrl}
      alt={file.name}
      className="max-h-[90px] max-w-full rounded object-contain"
      onError={() => setFailed(true)}
    />
  );
}

function InlineRenameInput({
  initialName,
  isFolder,
  onConfirm,
  onCancel,
}: {
  initialName: string;
  isFolder: boolean;
  onConfirm: (newName: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);
  const committedRef = useRef(false);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    if (isFolder) {
      el.select();
    } else {
      const dotIdx = initialName.lastIndexOf('.');
      el.setSelectionRange(0, dotIdx > 0 ? dotIdx : initialName.length);
    }
  }, [initialName, isFolder]);

  function commit() {
    if (committedRef.current) return;
    committedRef.current = true;
    const trimmed = value.trim();
    if (trimmed && trimmed !== initialName) {
      onConfirm(trimmed);
    } else {
      onCancel();
    }
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      maxLength={MAX_NAME_BYTES}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      }}
      onBlur={commit}
      onClick={(e) => e.stopPropagation()}
      className="w-full rounded border border-blue-400 bg-white px-1 py-0.5 text-xs text-slate-700 text-center outline-none ring-1 ring-blue-300"
    />
  );
}

interface FileGridProps {
  patientId: number;
  files: PatientFile[];
  currentFolder: string;
  onNavigate: (folder: string) => void;
  onFileClick: (file: PatientFile) => void;
  selectedFiles: Set<string>;
  onToggleSelect: (name: string) => void;
  renamingFile?: string | null;
  onRename?: (file: PatientFile, newName: string) => void;
  onRenameCancel?: () => void;
}

export function FileGrid({
  patientId,
  files,
  currentFolder,
  onNavigate,
  onFileClick,
  selectedFiles,
  onToggleSelect,
  renamingFile,
  onRename,
  onRenameCancel,
}: FileGridProps) {
  const handleTileClick = useCallback((file: PatientFile) => {
    if (file.type === 'folder') {
      const path = currentFolder ? `${currentFolder}/${file.name}` : file.name;
      onNavigate(path);
    } else {
      onFileClick(file);
    }
  }, [currentFolder, onNavigate, onFileClick]);

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3">
      {currentFolder && (
        <button
          type="button"
          onClick={() => {
            const parts = currentFolder.split('/');
            parts.pop();
            onNavigate(parts.join('/'));
          }}
          className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-4 h-[170px] hover:bg-slate-100 transition-colors"
        >
          <Folder className="h-12 w-12 text-slate-400 mb-2" />
          <span className="text-xs font-medium text-slate-500">Back</span>
        </button>
      )}

      {files.map((file) => {
        const Icon = file.type === 'folder' ? Folder : (ICON_MAP[file.icon] ?? File);
        const isSelected = selectedFiles.has(file.name);

        return (
          <div
            key={file.name}
            className={`relative flex flex-col items-center rounded-xl border bg-white p-3 h-[170px] cursor-pointer transition-all hover:shadow-md ${
              isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => { e.stopPropagation(); onToggleSelect(file.name); }}
              onClick={(e) => e.stopPropagation()}
              className="absolute top-2 left-2 rounded border-slate-300 z-10"
            />
            <div
              className="flex-1 flex items-center justify-center w-full overflow-hidden"
              onClick={() => handleTileClick(file)}
            >
              {file.type === 'file' && file.isImage ? (
                <ThumbnailImage patientId={patientId} file={file} folder={currentFolder} />
              ) : (
                <Icon className={`h-12 w-12 ${file.type === 'folder' ? 'text-amber-500' : 'text-slate-400'}`} />
              )}
            </div>
            <div className="w-full mt-1 text-center" onClick={() => { if (renamingFile !== file.name) handleTileClick(file); }}>
              {renamingFile === file.name ? (
                <InlineRenameInput
                  initialName={file.name}
                  isFolder={file.type === 'folder'}
                  onConfirm={(newName) => onRename?.(file, newName)}
                  onCancel={() => onRenameCancel?.()}
                />
              ) : (
                <p className="text-xs font-medium text-slate-700 truncate" title={file.name}>{file.name}</p>
              )}
              <p className="text-[10px] text-slate-400">
                {file.type === 'folder' ? 'Folder' : formatSize(file.size)}
                {file.modifiedAt ? ` · ${formatDate(file.modifiedAt)}` : ''}
              </p>
            </div>
          </div>
        );
      })}

      {files.length === 0 && (
        <div className="col-span-full py-12 text-center">
          <File className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">
            {currentFolder ? 'This folder is empty.' : 'No files found. Upload files or create a folder.'}
          </p>
        </div>
      )}
    </div>
  );
}
